/**
 * StopPills — compact pills for a multi-stop day (job name · hours · SERVICE/CONTRACT tag).
 * Tap a pill to open that stop's full details. Shared by the dispatch board cell and the
 * tech's Today view so both read the same way. Pills reorder by drag (optional).
 */
import { useState } from "react";
import { GripVertical } from "lucide-react";
import { JobTag, bandColor, isContract } from "@/components/dispatch/JobTag";
import { Hi } from "@/components/common/Highlight";
import { CrewBadge } from "@/components/tech/CrewLine";

export function StopPills({ stops, tech, activeId, onPick, onReorder, size = "sm", testIdPrefix = "stop-pill" }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const canDrag = typeof onReorder === "function";
  const drop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = stops.map(s => s.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    onReorder(ids);
    setDragId(null); setOverId(null);
  };
  const h = size === "lg" ? "min-h-[36px] text-[11px] md:text-xs" : "min-h-[22px] text-[9px]";
  return (
    <div className="flex flex-col gap-1" data-testid={`${testIdPrefix}-list`} data-count={stops.length}>
      {stops.map((s, i) => {
        const active = s.id === activeId;
        const color = bandColor(s, tech);
        return (
          <button
            key={s.id}
            type="button"
            draggable={canDrag}
            onDragStart={(e) => { if (!canDrag) return; e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; setDragId(s.id); }}
            onDragOver={(e) => { if (!canDrag || !dragId) return; e.preventDefault(); e.stopPropagation(); setOverId(s.id); }}
            onDragLeave={() => setOverId(null)}
            onDrop={(e) => { if (!canDrag) return; e.preventDefault(); e.stopPropagation(); drop(s.id); }}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onClick={(e) => { e.stopPropagation(); onPick?.(s, e); }}
            className={`relative w-full flex ${size === "lg" ? "items-center" : "flex-wrap items-center gap-y-0.5"} gap-x-1 rounded-md border pl-2 pr-1.5 py-0.5 text-left touch-manipulation transition-colors bg-card text-card-foreground ${h} ${
              active ? "ring-2 ring-offset-1" : "hover:brightness-110"
            } ${overId === s.id && dragId && dragId !== s.id ? "ring-2 ring-blue-400" : ""}`}
            style={{ borderColor: `${color}66`, boxShadow: `inset 3px 0 0 ${color}`, ...(active ? { "--tw-ring-color": color } : {}) }}
            data-testid={`${testIdPrefix}-${s.id}`}
            data-kind={isContract(s) ? "contract" : "service"}
            data-order={i}
            title={`${s.wo_title || "Untitled"} — tap for details`}
          >
            <Hi className={`font-semibold truncate min-w-0 ${size === "lg" ? "flex-1" : "basis-full"}`} text={s.wo_title || "Untitled"} />
            {s.hours != null && <span className="text-muted-foreground font-medium shrink-0">{s.hours}h</span>}
            <JobTag d={s} size={size === "lg" ? "sm" : "xs"} testId={`${testIdPrefix}-tag-${s.id}`} />
            <CrewBadge crew={s.crew} testId={`crew-badge-${s.id}`} />
            {canDrag && <GripVertical className="w-3 h-3 text-slate-300 shrink-0 cursor-grab" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}
